import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook to synchronize TextEditor state with Craft.js props, handling undo/redo.
 *
 * TextEditor (Lexical) only reads `initialValue` on mount, so external changes
 * (like undo/redo from Craft.js) won't update the editor visually. This hook:
 *
 * 1. Tracks the last value sent to Craft.js via `onUpdate`
 * 2. Detects external changes (when prop differs from what we last sent)
 * 3. Increments a key to force TextEditor to remount with the new value
 * 4. Provides a memoized `onUpdate` handler
 *
 * Pass `propValue` as the current prop value from Craft.js (e.g., props.text)
 * and `setPropValue` as the callback to update the prop in Craft.js.
 * Returns `editorKey` and `handleUpdate` to pass to TextEditor's key and onUpdate props.
 *
 * @example
 * ```tsx
 * const { editorKey, handleUpdate } = useSyncTextEditorState(
 *   props.text,
 *   (newContent) => setProp((p) => { p.text = newContent; })
 * );
 *
 * <TextEditor
 *   key={editorKey}
 *   initialValue={props.text}
 *   onUpdate={handleUpdate}
 * />
 * ```
 */
export function useSyncTextEditorState(
  propValue: string,
  setPropValue: (newValue: string) => void
) {
  // Track the last value we sent to Craft.js via onUpdate
  const lastSentValueRef = useRef<string>(propValue);

  // Key to force TextEditor remount when external changes happen (e.g., undo/redo)
  const [editorKey, setEditorKey] = useState(0);

  // Detect external changes (like undo) by comparing propValue with what we last sent
  // If they differ, the change came from outside, so force TextEditor to remount
  useEffect(() => {
    if (propValue !== lastSentValueRef.current) {
      setEditorKey((k) => k + 1);
      lastSentValueRef.current = propValue;
    }
  }, [propValue]);

  // Memoized update handler that tracks what we send
  const handleUpdate = useCallback(
    (newContent: string) => {
      lastSentValueRef.current = newContent;
      setPropValue(newContent);
    },
    [setPropValue]
  );

  return { editorKey, handleUpdate };
}
